import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { forkJoin, Observable, of, throwError } from "rxjs";
import { filter, map, switchMap, take } from "rxjs/operators";
import { Category } from "../../models";
import { AppvizerUrlService } from '../appvizer-url';
import { DatoCmsService } from "../dato-cms";

@Injectable()
export class MagazineUrlService {

  private readonly previewQuery = "?preview=true";

  constructor(
    private readonly appvizerUrl: AppvizerUrlService,
    private readonly cms: DatoCmsService,
    private readonly http: HttpClient
  ) {
  }

  getPreviewUrl(): Observable<string> {
    return forkJoin([
      this.cms.getPluginInitializer(),
      this.appvizerUrl.initialized$.pipe(filter(value => value), take(1))
    ]).pipe(
      switchMap(([plugin]) => {
        let baseUrl$: Observable<string | null> = of(null);

        const alias = plugin.getFieldValue("alias")[plugin.locale];

        if (!alias) {
          return throwError("No alias is set");
        }

        if (plugin.itemType.attributes.api_key === "news") {
          baseUrl$ = of(this.getNewsBaseUrl());
        } else {
          if (!plugin.getFieldValue("category") || !plugin.getFieldValue("category")[plugin.locale]) {
            return throwError("No category selected on article");
          }

          const category: Category = JSON.parse(plugin.getFieldValue("category")[plugin.locale]);
          baseUrl$ = this.getArticleBaseUrl(category.identifier);
        }
        return forkJoin([
          baseUrl$,
          of(alias),
        ]);
      }),
      map(([baseUrl, alias]) => {
        if (!baseUrl) {
          throw new Error('Article or news base url could not be constructed');
        }

        return `${baseUrl}/${alias}${this.previewQuery}`;
      })
    );
  }

  private getNewsBaseUrl(): string {
    return `${this.appvizerUrl.domainUrl}${this.appvizerUrl.newsPath}`;
  }

  private getArticleBaseUrl(categoryId: string): Observable<string | null> {
    return this.http.post<any>(this.appvizerUrl.softwareCenterUrl + '/graphql',
      {
        query: `{
          category(id: "${categoryId}") {
            categoryDomain(domain: ${this.appvizerUrl.domainId}) {
              path
            }
          }
        }`
      }
    )
      .pipe(
        map(res => {
          const categoryDomain = res?.data?.category?.categoryDomain;
          if (!categoryDomain?.path) {
            return null;
          }

          return `${this.appvizerUrl.domainUrl}${this.appvizerUrl.magazinePath}${categoryDomain.path}`;
        })
      );
  }
}
